/**
 * Lead Scoring Hook - Track pages viewed before form submission
 * Stores viewed pages in localStorage and sends with form submissions
 */

const STORAGE_KEY = 'reshapers_viewed_pages';
const MAX_PAGES = 20; // Keep last 20 pages

export const trackPageView = (pagePath, pageTitle) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const pages = stored ? JSON.parse(stored) : [];
    
    // Add new page view
    const newView = {
      path: pagePath,
      title: pageTitle,
      timestamp: new Date().toISOString()
    };
    
    // Avoid duplicates for same page within 5 minutes
    const recentDuplicate = pages.find(p => 
      p.path === pagePath && 
      new Date() - new Date(p.timestamp) < 5 * 60 * 1000
    );
    
    if (!recentDuplicate) {
      pages.push(newView);
      // Keep only last MAX_PAGES
      const trimmed = pages.slice(-MAX_PAGES);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    }
  } catch (e) {
  }
};

export const getViewedPages = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
};

export const clearViewedPages = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore
  }
};

// Calculate lead score based on pages viewed
export const calculateLeadScore = (pages) => {
  if (!pages || pages.length === 0) return 0;
  
  let score = 0;
  const weights = {
    '/solutions': 15,
    '/contact': 20,
    '/workshops': 15,
    '/insights': 5,
    '/impact': 10,
    '/about': 5,
    '/retreats': 15,
    '/ecd-certification': 10,
    '/go-team-blue-wave': 10,
  };
  
  // Score based on pages visited
  pages.forEach(page => {
    const basePath = '/' + (page.path.split('/')[1] || '');
    score += weights[basePath] || 2;
  });
  
  // Bonus for multiple visits
  if (pages.length >= 5) score += 10;
  if (pages.length >= 10) score += 15;
  
  // Bonus for visiting solutions detail pages
  const solutionDetails = pages.filter(p => p.path.startsWith('/solutions/'));
  score += solutionDetails.length * 10;
  
  // Cap at 100
  return Math.min(score, 100);
};

// Get journey summary for CMS
export const getJourneySummary = (pages) => {
  if (!pages || pages.length === 0) return 'Direct submission';
  
  const uniquePaths = [...new Set(pages.map(p => p.path))];
  const categories = {
    solutions: uniquePaths.filter(p => p.startsWith('/solutions')).length,
    insights: uniquePaths.filter(p => p.startsWith('/insights')).length,
    workshops: uniquePaths.filter(p => p.startsWith('/workshops')).length,
    impact: uniquePaths.filter(p => p.startsWith('/impact')).length,
  };
  
  const interests = [];
  if (categories.solutions > 0) interests.push('Solutions');
  if (categories.workshops > 0) interests.push('Workshops');
  if (categories.insights > 0) interests.push('Insights');
  if (categories.impact > 0) interests.push('Impact/Cases');
  
  return interests.length > 0 
    ? `Interested in: ${interests.join(', ')} (${pages.length} pages viewed)`
    : `Browsed ${pages.length} pages`;
};
